import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sunsite.it';
// Remove /api suffix since we're using compatibility routes
const IMAGE_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sunsite.it';

// Add debugging for image configuration
console.log('Image configuration:', {
    API_BASE_URL,
    IMAGE_BASE_URL,
    uploadsPath: `${IMAGE_BASE_URL}/uploads/`,
    environment: process.env.NODE_ENV
});

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('authToken');
            window.location.href = '/admin/login';
        }
        return Promise.reject(error);
    }
);

// Admin API endpoints
export const adminAPI = {
    login: (credentials) => api.post(`/admin/login`, credentials),

    // Website Images
    getWebsiteImages: () => api.get('/admin/website-images'),
    uploadWebsiteImage: (imageName, imageFile) => {
        const formData = new FormData();
        formData.append('image', imageFile);
        return api.post(`/admin/website-images/${imageName}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },
    updateWebsiteImage: (imageName, imageData) => api.put(`/admin/website-images/${imageName}`, imageData),

    // Gallery - now supports both images and videos
    getGalleryImages: () => api.get('/admin/gallery'),
    uploadGalleryMedia: (mediaFile, descriptions = {}, folderId = null) => {
        const { de = '', en = '', it = '' } = descriptions || {};
        const formData = new FormData();
        formData.append('image', mediaFile); // backward compatibility field name
        formData.append('description_de', de);
        formData.append('description_en', en);
        formData.append('description_it', it);
        if (folderId) {
            formData.append('folder_id', folderId);
        }
        // also keep legacy 'description' for older servers (use de)
        if (de) formData.append('description', de);
        return api.post('/admin/gallery/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    deleteGalleryImage: (imageId) => api.delete(`/admin/gallery/${imageId}`),
    updateGalleryImage: (imageId, descriptions = {}, folderId = null) => {
        const { de = null, en = null, it = null } = descriptions || {};
        const t = (v) => (typeof v === 'string' ? v.trim() : v);
        const deT = t(de);
        const enT = t(en);
        const itT = t(it);

        const formData = new FormData();

        const allProvided = [de, en, it].every(v => v !== null && v !== undefined);
        const allEmpty = [deT, enT, itT].every(v => v === '' || v === null || v === undefined);

        // If user cleared all description fields, tell the backend to wipe descriptions
        if (allProvided && allEmpty) {
            formData.append('clear_all_descriptions', 'true');
        }

        // Append fields that are intended to be updated (null means ignore)
        if (de !== null) formData.append('description_de', deT ?? '');
        if (en !== null) formData.append('description_en', enT ?? '');
        if (it !== null) formData.append('description_it', itT ?? '');
        if (folderId !== null) {
            formData.append('folder_id', folderId);
        }
        // keep legacy for compatibility (use trimmed de)
        if (de !== null && !(allProvided && allEmpty)) formData.append('description', deT || '');

        return api.put(`/admin/gallery/${imageId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },

    // Collage endpoints
    getCollages: () => api.get('/admin/gallery/collages'),
    createCollage: ({ titles = {}, descriptions = {}, folderId = null, files = [] } = {}) => {
        const { de: titleDe = '', en: titleEn = '', it: titleIt = '' } = titles || {};
        const { de: descDe = '', en: descEn = '', it: descIt = '' } = descriptions || {};
        const formData = new FormData();
        formData.append('title_de', titleDe);
        formData.append('title_en', titleEn);
        formData.append('title_it', titleIt);
        formData.append('description_de', descDe);
        formData.append('description_en', descEn);
        formData.append('description_it', descIt);
        if (folderId !== null && folderId !== undefined && folderId !== '') {
            formData.append('folder_id', folderId);
        }
        (files || []).forEach((file) => formData.append('images', file));
        return api.post('/admin/gallery/collages', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    updateCollage: (collageId, { titles = {}, descriptions = {}, folderId = null, imageOrder = null } = {}) => {
        const formData = new FormData();
        const applyValue = (key, value) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        };
        if (titles) {
            if (Object.prototype.hasOwnProperty.call(titles, 'de')) {
                applyValue('title_de', titles.de ?? '');
            }
            if (Object.prototype.hasOwnProperty.call(titles, 'en')) {
                applyValue('title_en', titles.en ?? '');
            }
            if (Object.prototype.hasOwnProperty.call(titles, 'it')) {
                applyValue('title_it', titles.it ?? '');
            }
        }
        if (descriptions) {
            if (Object.prototype.hasOwnProperty.call(descriptions, 'de')) {
                applyValue('description_de', descriptions.de ?? '');
            }
            if (Object.prototype.hasOwnProperty.call(descriptions, 'en')) {
                applyValue('description_en', descriptions.en ?? '');
            }
            if (Object.prototype.hasOwnProperty.call(descriptions, 'it')) {
                applyValue('description_it', descriptions.it ?? '');
            }
        }
        if (folderId !== undefined) {
            applyValue('folder_id', folderId === null ? '' : folderId);
        }
        if (Array.isArray(imageOrder)) {
            applyValue('image_order', JSON.stringify(imageOrder));
        }
        return api.put(`/admin/gallery/collages/${collageId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    addCollageImages: (collageId, files = []) => {
        const formData = new FormData();
        (files || []).forEach((file) => formData.append('images', file));
        return api.post(`/admin/gallery/collages/${collageId}/images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    deleteCollageImage: (collageId, imageId) => api.delete(`/admin/gallery/collages/${collageId}/images/${imageId}`),
    deleteCollage: (collageId) => api.delete(`/admin/gallery/collages/${collageId}`),

    // Add debug endpoint for checking media availability
    debugImage: (imagePath) => {
        let cleanPath = imagePath;
        if (imagePath.startsWith('/uploads/')) {
            cleanPath = imagePath.replace('/uploads/', '');
        } else if (imagePath.startsWith('uploads/')) {
            cleanPath = imagePath.replace('uploads/', '');
        }
        return api.get(`/debug/uploads/${cleanPath}`);
    }
};

export const normalizeGalleryItem = (item) => {
    if (!item) return item;
    const normalized = { ...item };
    if (Array.isArray(normalized.collage_images)) {
        normalized.collage_images = [...normalized.collage_images]
            .map((img, index) => ({
                ...img,
                position: typeof img.position === 'number' ? img.position : index,
            }))
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    return normalized;
};

// Public API endpoints
export const publicAPI = {
    getWebsiteImage: (imageName) => api.get(`/website-images/${imageName}`),

    // Gallery
    getGalleryImages: (page = 1, perPage = 12) => {
        const params = new URLSearchParams({ page: page.toString(), per_page: perPage.toString() });
        return api.get(`/gallery?${params}`).then(res => {
            res.data = Array.isArray(res.data) ? res.data.map(normalizeGalleryItem) : res.data;
            return res;
        });
    },
    getGalleryCount: () => api.get('/gallery/count'),

    // New public folder endpoints
    getPublicGalleryFolders: () => api.get('/gallery/folders').then(res => res.data),
    getImagesInFolder: (folderId, page = 1, perPage = 12) =>
        api
            .get(`/gallery/folders/${folderId}/images?page=${page}&per_page=${perPage}`)
            .then(res => (Array.isArray(res.data) ? res.data.map(normalizeGalleryItem) : res.data)),
    getImagesInFolderCount: (folderId) => api.get(`/gallery/folders/${folderId}/count`).then(res => res.data),
    getUncategorizedImages: (page = 1, perPage = 100) =>
        api
            .get(`/gallery/uncategorized-images?page=${page}&per_page=${perPage}`)
            .then(res => (Array.isArray(res.data) ? res.data.map(normalizeGalleryItem) : res.data)),
    getUncategorizedImagesCount: () => api.get('/gallery/uncategorized-images/count').then(res => res.data),
};

// New admin folder functions
export const getAdminGalleryFolders = async (token) => {
    const response = await api.get('/admin/gallery/folders', {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
};

export const createGalleryFolder = async (formData, token) => {
    const response = await api.post('/admin/gallery/folders', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
        }
    });
    return response.data;
};

export const updateGalleryFolder = async (folderId, formData, token) => {
    const response = await api.put(`/admin/gallery/folders/${folderId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
        }
    });
    return response.data;
};

export const uploadFolderCoverImage = async (folderId, formData, token) => {
    const response = await api.post(`/admin/gallery/folders/${folderId}/upload-cover`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
        }
    });
    return response.data;
};

export const deleteGalleryFolder = async (folderId, token) => {
    const response = await api.delete(`/admin/gallery/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
};


// Export media URL helper function (replaces getImageUrl for media files)
export const getMediaUrl = (mediaPath) => {
    if (!mediaPath) {
        return null;
    }

    // Handle array of media - return first item
    if (Array.isArray(mediaPath)) {
        if (mediaPath.length === 0) {
            return null;
        }
        mediaPath = mediaPath[0];
    }

    // Clean the path - remove any duplicate slashes
    let cleanPath = mediaPath.toString().trim();

    // If path already starts with http, return as is
    if (cleanPath.startsWith('http')) {
        return cleanPath;
    }

    // Handle static assets (like /Solarpanele.webp) - serve from frontend build
    if (cleanPath.startsWith('/') && !cleanPath.startsWith('/uploads/')) {
        return `${IMAGE_BASE_URL}${cleanPath}`;
    }

    // Ensure path starts with /uploads/ for uploaded media
    if (!cleanPath.startsWith('/uploads/')) {
        if (cleanPath.startsWith('uploads/')) {
            cleanPath = '/' + cleanPath;
        } else {
            cleanPath = '/uploads/' + cleanPath;
        }
    }

    // Remove any double slashes and ensure single leading slash
    cleanPath = cleanPath.replace(/\/+/g, '/');
    if (!cleanPath.startsWith('/')) {
        cleanPath = '/' + cleanPath;
    }

    const fullUrl = `${IMAGE_BASE_URL}${cleanPath}`;
    console.log('Generated media URL:', fullUrl, 'from path:', mediaPath);
    return fullUrl;
};

// Keep getImageUrl for backward compatibility
export const getImageUrl = getMediaUrl;

// Add specific function for website images
export const getWebsiteImageUrl = getMediaUrl;

// Add function to test image availability with better error handling
export const testImageUrl = async (url) => {
    try {
        const response = await fetch(url, {
            method: 'HEAD',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
                'Accept': 'image/*,video/*,*/*',
                'Cache-Control': 'no-cache'
            }
        });

        console.log('Image test response:', {
            url,
            status: response.status,
            ok: response.ok,
            contentType: response.headers.get('content-type')
        });

        const ct = response.headers.get('content-type') || '';
        const isMedia = ct.startsWith('image/') || ct.startsWith('video/');
        return response.ok && (response.status === 200 || response.status === 304) && isMedia;
    } catch (error) {
        console.error('Error testing image URL:', url, error);
        return false;
    }
};

// Export function to get all image URLs
export const getImageUrls = (imagePaths) => {
    if (!imagePaths) return [];

    // Handle single image path
    if (typeof imagePaths === 'string') {
        const url = getImageUrl(imagePaths);
        return url ? [url] : [];
    }

    // Handle array of image paths
    if (Array.isArray(imagePaths)) {
        return imagePaths
            .filter(path => path)
            .map(path => getImageUrl(path))
            .filter(url => url);
    }

    return [];
};

// Helper function to check if a file is a video
export const isVideoFile = (filename) => {
    if (!filename) return false;
    const ext = filename.toLowerCase().split('.').pop();
    const videoExtensions = ['mp4', 'webm', 'ogg', 'ogv', 'avi', 'mov', 'wmv', 'flv', '3gp', 'mkv'];
    return videoExtensions.includes(ext);
};

// Helper function to check if a file is an image
export const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.toLowerCase().split('.').pop();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'tif'];
    return imageExtensions.includes(ext);
};

// Helper function to get media type
export const getMediaType = (item) => {
    // Check if the item has file_type property (from database)
    if (item && item.file_type) {
        return item.file_type;
    }

    // Fallback to filename-based detection
    const filename = item?.filename || item?.original_filename || item;
    if (isVideoFile(filename)) {
        return 'video';
    } else if (isImageFile(filename)) {
        return 'image';
    }
    return 'unknown';
};

export default api;